import React from 'react'
import { InferGetServerSidePropsType, Redirect } from 'next'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import {
  NationalStaticData,
  fetchNationalStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../../types/next'
import { makeApolloClient } from '../../../lib/apolloClient'
import {
  ContentDetail,
  getContentDetail,
} from '../../../lib/get-content-detail'
import { getDefaultNationalMetaData } from '../../../../../src/shared/helpers/get-default-national-metadata'
import { StoryContentDetail } from '../../../components/page-templates/content-detail/story'
import { META_FALLBACK_IMAGE } from '../../../../../src/shared/constants'
import { shouldRedirectExpiredContent } from '../../../lib/redirect-expired-content'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import Breadcrumbs from '@scentregroup/shared/hub-components/breadcrumbs'
import { BreadcrumbContainer } from '../../../components/breadcrumb-container'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { getNewsArticleStructuredDataFromStory } from '../../../lib/schema/get-news-article-structured-data-from-story'

export interface INationalStoryPageProps {
  country: ReturnType<typeof localeToCountry>
  topStripProps: ITopStripProps | null
  story: ContentDetail['story']
  headerStaticData: NationalStaticData
}

function getRedirectForStoryWithMismatchedSlug(
  ctx: GetServerSidePropsContext<{ id: string; slug: string }>,
  story: ContentDetail['story']
): Redirect | null {
  if (!story.slug) {
    return null
  }

  if (story.slug !== ctx.params?.slug) {
    const e = encodeURIComponent
    return {
      destination: `/story/${e(story.sys.id)}/${e(story.slug)}`,
      permanent: true,
    }
  }
  return null
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext<{ id: string; slug: string }>
): Promise<GetServerSidePropsResult<INationalStoryPageProps>> => {
  setCacheHeaders(ctx.res)
  const country = localeToCountry(ctx.locale)
  const id = ctx.params?.id
  if (!id) {
    return {
      notFound: true,
    }
  }

  const apollo = makeApolloClient()

  const [headerStaticData, story, topStripProps] = await Promise.all([
    fetchNationalStaticData(apollo, { country }),
    getContentDetail(apollo, 'story', { id }),
    getTopStripProps(apollo, country),
  ])

  if (!story) {
    return {
      notFound: true,
    }
  }

  const redirect = shouldRedirectExpiredContent(story.hideAt, '/')

  if (redirect) {
    return redirect
  }

  const redirectObj = getRedirectForStoryWithMismatchedSlug(ctx, story)

  if (redirectObj) {
    return { redirect: redirectObj }
  }

  return {
    props: {
      country,
      topStripProps,
      headerStaticData,
      story,
    },
  }
}

const StoryDetailPage: React.FC<
  React.PropsWithChildren<
    InferGetServerSidePropsType<typeof getServerSideProps>
  >
> = props => {
  const { country, topStripProps, story, headerStaticData } = props

  const metaTitle = story.title
  const metaDescription = getDefaultNationalMetaData({
    country,
    type: 'default',
  }).description
  const metaCanonical = completelyCanonicalUrl(useGetHrefContext()())

  const metaData = {
    title: metaTitle,
    description: metaDescription,
    canonical: metaCanonical,
    openGraph: {
      url: metaCanonical,
      title: metaTitle,
      description: metaDescription,
      image: story.heroImageSet?.master?.url || META_FALLBACK_IMAGE,
    },
  }

  const schema = story.heroImageSet?.master
    ? getNewsArticleStructuredDataFromStory(story, metaCanonical)
    : undefined

  return (
    <>
      <SGLinkedData item={schema} />
      <Layout
        country={country}
        topStrip={topStripProps}
        centre={undefined}
        header={{
          variant: 'national',
          data: headerStaticData,
          hasBottomMargin: [false, false, false],
          logoText: null,
          displayEmptyCartInHeader: true,
        }}
        metaData={{ ...metaData }}
      >
        <BreadcrumbContainer>
          <Breadcrumbs
            breadcrumbs={[
              { title: 'Home', linkTo: '/' },
              { title: 'The Edit', linkTo: '/the-edit' },
              { title: metaData.title },
            ]}
          />
        </BreadcrumbContainer>
        <StoryContentDetail content={story} />
      </Layout>
    </>
  )
}

export default StoryDetailPage
